import { IDBRecord, ITableConfigIndexedDB, ITemporaryData } from './types'

const OLD_DB_NAMES = ['mediaItemsCache', 'videoFrameCache', 'thumbnailsCache']

/**
 * Utility class for managing IndexedDB operations with a focus on video processing
 * data storage. Implements the singleton pattern to ensure consistent database
 * connections across the application.
 *
 * The class handles multiple table types for different aspects of video processing:
 * frames, video segments, encoded data, and raw pixel data.
 */
export class IndexedDB {
  /** Map of database instances keyed by database name */
  private static instanceMap: Map<string, IndexedDB> = new Map<
    string,
    IndexedDB
  >()

  /**
   * Gets or creates a singleton instance for the specified database name.
   *
   * @param DB_NAME - Name of the IndexedDB database to connect to.
   * @param tables  - Optional array of table configurations.
   * @returns The singleton IndexedDB instance for the given database.
   */
  public static getInstance(
    DB_NAME: string,
    tables: ITableConfigIndexedDB[] = []
  ): IndexedDB {
    this.deleteOldDatabases()
    if (!this.instanceMap.get(DB_NAME)) {
      this.instanceMap.set(DB_NAME, new this(DB_NAME, tables))
    }
    return this.instanceMap.get(DB_NAME)!
  }

  private static async deleteOldDatabases(): Promise<void> {
    await Promise.all(OLD_DB_NAMES.map(this.deleteDatabase))
  }

  public static deleteDatabase(DB_NAME: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME)

      request.onsuccess = () => {
        // DB deleted successfully (or didn’t exist at all)
        resolve()
      }

      request.onerror = () => {
        reject(request.error)
      }

      request.onblocked = () => {
        // eslint-disable-next-line no-console
        console.warn(
          `Delete blocked: another connection is open to "${DB_NAME}"`
        )
      }
    })
  }

  /** Current database version, used for schema migrations */
  private version: number | null = null

  /**
   * Private constructor to enforce singleton pattern.
   *
   * @param DB_NAME - Name of the IndexedDB database.
   */
  private constructor(
    private DB_NAME: string,
    private tables: ITableConfigIndexedDB[]
  ) {}

  public registerTable(tableConfig: ITableConfigIndexedDB): void {
    if (!this.tables.find(t => t.name === tableConfig.name)) {
      this.tables.push(tableConfig)
    }
  }

  public async getTableNames(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME)
      request.onsuccess = event => {
        const db = (event.target as IDBOpenDBRequest).result
        const names = Array.from(db.objectStoreNames)
        db.close()
        resolve(names)
      }
      request.onerror = event =>
        reject((event.target as IDBOpenDBRequest).error)
    })
  }

  /**
   * Opens a connection to the IndexedDB database, creating tables if necessary.
   * Handles version upgrades and ensures the requested table exists.
   *
   * @param dbTable - Name of the table that must exist in the database.
   * @returns Promise that resolves to an open IDBDatabase connection.
   */
  protected async openDatabase(
    dbTable: ITableConfigIndexedDB
  ): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        this.DB_NAME,
        this.version ? this.version : undefined
      )

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result

        for (const table of this.tables) {
          if (!db.objectStoreNames.contains(table.name)) {
            const store = db.createObjectStore(table.name, table.options)
            for (const index of table.indexes || []) {
              store.createIndex(index.name, index.keyPath, index.options)
            }
          }
        }
      }

      request.onsuccess = async event => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!this.version) {
          this.version = db.version
        }
        if (!db.objectStoreNames.contains(dbTable.name)) {
          this.version++
          db.close()
          const newDb = await this.openDatabase(dbTable)
          resolve(newDb)
        } else {
          resolve(db)
        }
      }

      request.onerror = event => {
        reject((event.target as IDBOpenDBRequest).error)
      }
    })
  }

  /**
   * Saves a new record to the specified table.
   *
   * @typeParam D - Type of the data being saved.
   * @param dbTable - Target table for the record.
   * @param data    - Data object to save.
   * @returns Promise that resolves to the auto-generated record ID.
   */
  public async saveRecord<D>(
    dbTable: string,
    data: D & ITemporaryData
  ): Promise<IDBValidKey> {
    const targetTable = this.getTargetTable(dbTable)
    return new Promise((resolve, reject) => {
      this.openDatabase(targetTable).then(db => {
        const transaction = db.transaction(targetTable.name, 'readwrite')
        const store = transaction.objectStore(targetTable.name)
        if (targetTable.expirationTime) {
          data.timestamp = Date.now()
          data.expirationTime = Date.now() + targetTable.expirationTime
        }
        const request = store.add(data)
        request.onsuccess = () => {
          resolve(request.result)
        }
        request.onerror = () => reject(request.error)
      })
    })
  }

  /**
   * Retrieves a single record by its ID from the specified table.
   *
   * @typeParam D - Type of the data being retrieved.
   * @param id      - Unique identifier of the record.
   * @param dbTable - Source table for the record.
   * @returns Promise that resolves to the record data.
   */
  public async readRecord<D = unknown>(
    dbTable: string,
    id: IDBValidKey
  ): Promise<D> {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readonly')
        const store = transaction.objectStore(targetTable.name)
        const request = store.get(id)

        request.onsuccess = () => {
          const result = request.result as D & ITemporaryData
          if (!result) {
            resolve(result)
            return
          }
          if (
            targetTable.expirationTime &&
            result.expirationTime &&
            result.timestamp &&
            result.timestamp + result.expirationTime < Date.now()
          ) {
            this.deleteRecord(dbTable, id)
            reject(new Error('Record expired'))
          } else {
            resolve(result)
          }
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
    })
  }

  /**
   * Retrieves all records from the specified table.
   *
   * @typeParam D - Type of the data being retrieved.
   * @param dbTable - Source table for the records.
   * @returns Promise that resolves to an array of all records.
   */
  public async readAllRecords<D = unknown>(dbTable: string): Promise<D[]> {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readonly')
        const store = transaction.objectStore(targetTable.name)
        const request = store.getAll()

        request.onsuccess = () => {
          const result = request.result as (D & ITemporaryData & IDBRecord)[]
          if (!result.length) {
            resolve([])
            return
          }
          const filteredResult = result.filter(item => {
            if (
              item.expirationTime &&
              item.timestamp &&
              item.timestamp + item.expirationTime < Date.now()
            ) {
              const indexes = targetTable.indexes || []
              this.deleteRecord(dbTable, item[indexes[0].keyPath as string])
              return false
            }
            return true
          })
          resolve(filteredResult)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
    })
  }

  /**
   * Deletes a record by its ID from the specified table.
   *
   * @param dbTable - Target table containing the record.
   * @param id      - Unique identifier of the record to delete.
   * @returns Promise that resolves when the deletion is complete.
   */
  public async deleteRecord(dbTable: string, id: IDBValidKey): Promise<void> {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readwrite')
        const store = transaction.objectStore(targetTable.name)
        const request = store.delete(id)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    })
  }

  /**
   * Updates an existing record by merging new data with the existing record.
   *
   * @typeParam D - Type of the data being updated.
   * @param dbTable - Target table containing the record.
   * @param id      - Unique identifier of the record to update.
   * @param data    - Partial data object to merge with existing record.
   * @returns Promise that resolves when the update is complete.
   */
  public async updateRecord<D>(dbTable: string, id: IDBValidKey, data: D) {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readwrite')
        const store = transaction.objectStore(targetTable.name)
        const getRequest = store.get(id)
        const tableConfig = this.tables.find(t => t.name === dbTable)!
        getRequest.onsuccess = () => {
          const existingData: D = getRequest.result
          if (existingData) {
            const updatedData = { ...existingData, ...data }
            const updateRequest = !tableConfig.options?.keyPath
              ? store.put(updatedData, id)
              : store.put(updatedData)

            updateRequest.onsuccess = resolve
            updateRequest.onerror = () => reject(updateRequest.error)
          } else {
            reject('Record not found')
          }
        }
        getRequest.onerror = () => reject(getRequest.error)
      })
    })
  }

  /**
   * Removes all records from the specified table.
   *
   * @param dbTable - Target table to clear.
   * @returns Promise that resolves when the table is cleared.
   */
  public async clearTable(dbTable: string): Promise<void> {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readwrite')
        const store = transaction.objectStore(targetTable.name)
        const request = store.clear()
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    })
  }

  /**
   * Checks if a record exists in the specified table.
   *
   * @param dbTable - Target table containing the record.
   * @param id      - Unique identifier of the record to check.
   * @returns Promise that resolves to true if the record exists, false otherwise.
   */
  public async hasRecord(dbTable: string, id: IDBValidKey): Promise<boolean> {
    const targetTable = this.getTargetTable(dbTable)
    return this.openDatabase(targetTable).then(db => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(targetTable.name, 'readonly')
        const store = transaction.objectStore(targetTable.name)
        const request = store.getKey(id)
        request.onsuccess = () => resolve(!!request.result)
        request.onerror = () => reject(request.error)
      })
    })
  }

  /**
   * Gets the target table configuration based on the provided table name.
   *
   * @param dbTable - Name of the table to retrieve.
   * @returns The target table configuration.
   * @throws Error if the specified table is not found in the database.
   */
  private getTargetTable(dbTable: string) {
    const targetTable = this.tables.find(t => t.name === dbTable)
    if (!targetTable) {
      const validTableNames = this.tables.map(t => t.name).join(', ')
      throw new Error(
        `Table ${dbTable} not found in ${this.DB_NAME}. Valid table names are: ${validTableNames}`
      )
    }
    return targetTable
  }
}
